import { Link } from "react-router";
import { useT } from "../../lib/use-t";
import { getVocabLibrary } from "../../data/vocab/library";
import { TOPIC_DISPLAY } from "../../data/vocab/topic-display";
import { ALL_TOPIC_KEYS, type TopicKey } from "../../data/vocab/types";
import { TopicIcon } from "./TopicIcon";

// Topics that are not browseable as word lists from the landing grid:
// super-zinnen ships above the grid as its own card; templates-by-deel is
// prose-only with 0 entries (kept as a TopicKey so the parser/schema stay
// faithful to the master sheet, but it has no detail page yet).
const HIDDEN_FROM_GRID: ReadonlySet<TopicKey> = new Set<TopicKey>([
  "super-zinnen",
  "templates-by-deel",
]);

/** Client-side topic filter: matches the query against title + blurb. Empty
 *  query passes everything. No backend — pure in-memory filter. */
export function TopicGrid({ query = "" }: { query?: string }) {
  const t = useT();
  const lib = getVocabLibrary();
  const q = query.trim().toLowerCase();

  const topics = ALL_TOPIC_KEYS.filter((k) => !HIDDEN_FROM_GRID.has(k))
    .map((key) => ({
      key,
      title: TOPIC_DISPLAY[key].title,
      sub: TOPIC_DISPLAY[key].blurb,
      icon: TOPIC_DISPLAY[key].icon,
      count: lib.stats.byTopic[key] ?? 0,
    }))
    .filter(
      (topic) =>
        q === "" ||
        topic.title.toLowerCase().includes(q) ||
        topic.sub.toLowerCase().includes(q),
    );

  if (topics.length === 0) {
    return (
      <p className="font-serif-italic text-[var(--ink-muted)] py-6">
        {t("woorden.empty.noTopics")}
      </p>
    );
  }

  return (
    <ul className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-3">
      {topics.map((topic) => (
        <li key={topic.key}>
          <Link
            to={`/woorden/${topic.key}`}
            className="group flex h-full flex-col rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] p-4 sm:p-5 transition-[transform,box-shadow,border-color] duration-150 ease-out hover:-translate-y-0.5 hover:shadow-[var(--shadow)] hover:border-[var(--border-strong)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2"
          >
            <span className="mb-3 sm:mb-4 inline-grid place-items-center w-10 h-10 sm:w-[46px] sm:h-[46px] rounded-[14px] bg-[var(--accent-soft)] text-[var(--accent)]">
              <TopicIcon icon={topic.icon} />
            </span>
            <h3 className="text-base sm:text-lg font-semibold tracking-tight">{topic.title}</h3>
            <p className="mt-1 mb-3 sm:mb-4 text-sm text-[var(--ink-muted)] leading-snug line-clamp-2 sm:min-h-[34px]">
              {topic.sub}
            </p>
            <p className="mt-auto font-mono-badge text-xs text-[var(--ink-faint)] tabular-nums">
              {t("woorden.topicTotalFmt", { count: topic.count })}
            </p>
          </Link>
        </li>
      ))}
    </ul>
  );
}
